import { z } from 'zod';
import {
  EVAL_TENANT_ID,
  EVALUATION_DECISION_METRICS,
  EVALUATION_OVERLAY_MAX_BYTES,
  evaluationOverlayExceedsMaxBytes,
} from '@terra/api-common-constants';
import {
  EvaluationInferenceProviderIdSchema,
  EvaluationPlaybookOverridesSchema,
  EvaluationPlaybookPhasesFilterSchema,
  EvaluationRunStatusSchema,
  EvaluationSlotPatchSchema,
} from './evaluation-run-shared.schemas';

const EvaluationAccountIdSchema = z.string().min(1).max(200);

export const EvaluationExperimentConfigSchema = z.object({
  name: z.string().min(1).max(100),
  engine: z.enum(['neo', 'agents-v2', 'claude-code']),
  accountId: EvaluationAccountIdSchema.optional(),
  modelOverlay: z
    .object({
      enabled: z.literal(true),
      accountId: EvaluationAccountIdSchema.optional(),
      providerId: EvaluationInferenceProviderIdSchema.optional(),
      crafts: z.record(z.string(), EvaluationSlotPatchSchema),
      playbookPhases: EvaluationPlaybookPhasesFilterSchema.optional(),
      playbookOverrides: EvaluationPlaybookOverridesSchema.optional(),
    })
    .superRefine((overlay, ctx) => {
      if (evaluationOverlayExceedsMaxBytes(overlay)) {
        ctx.addIssue({
          code: 'custom',
          message: `modelOverlay exceeds ${EVALUATION_OVERLAY_MAX_BYTES} bytes`,
        });
      }
    })
    .optional(),
  providerId: EvaluationInferenceProviderIdSchema.optional(),
});

export const EvaluationDecisionMetricSchema = z.enum(
  EVALUATION_DECISION_METRICS,
);

export const EvaluationDecisionPolicySchema = z.object({
  metric: EvaluationDecisionMetricSchema.default('labelMatchRate'),
  minSamples: z.int().min(1).max(10_000).default(20),
  minDelta: z.number().min(0).max(1).default(0.05),
});

export const CreateEvaluationExperimentRequestSchema = z
  .object({
    applicationId: z.string().min(1).optional(),
    componentId: z.string().min(1).optional(),
    name: z.string().min(1).max(200),
    datasetId: z.string().min(1).optional(),
    datasetGroupId: z.string().min(1).optional(),
    imageTag: z.string().min(1).max(200).optional(),
    runsPerConfig: z.int().min(1).max(10).default(1),
    configs: z.array(EvaluationExperimentConfigSchema).min(1).max(10),
    baselineConfigName: z.string().min(1).max(100).optional(),
    decisionPolicy: EvaluationDecisionPolicySchema.optional(),
    scenarioConfig: z.record(z.string(), z.unknown()).optional(),
    optOutReuseConfigNames: z.array(z.string().min(1)).optional(),
    resetAppBetweenConfigs: z.boolean().optional(),
    povEnvironmentId: z.string().min(1).optional(),
    scenarioSource: z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('dataset'),
        datasetId: z.string().min(1),
        instantiationTenantId: z
          .literal(EVAL_TENANT_ID)
          .default(EVAL_TENANT_ID),
      }),
      z.object({
        kind: z.literal('datasetGroup'),
        datasetGroupId: z.string().min(1),
        instantiationTenantId: z
          .literal(EVAL_TENANT_ID)
          .default(EVAL_TENANT_ID),
      }),
    ]),
  })
  .superRefine((value, ctx) => {
    if (
      value.baselineConfigName &&
      !value.configs.some((config) => config.name === value.baselineConfigName)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['baselineConfigName'],
        message: 'baselineConfigName must match one of the experiment configs',
      });
    }

    if (value.resetAppBetweenConfigs && !value.povEnvironmentId) {
      ctx.addIssue({
        code: 'custom',
        path: ['povEnvironmentId'],
        message:
          'povEnvironmentId is required when resetAppBetweenConfigs is enabled',
      });
    }

    if (value.scenarioSource.kind === 'dataset') {
      if (!value.datasetId && !value.scenarioSource.datasetId) {
        ctx.addIssue({
          code: 'custom',
          path: ['datasetId'],
          message: 'datasetId is required for dataset scenarioSource',
        });
      }
    }

    if (value.scenarioSource.kind === 'datasetGroup') {
      if (!value.datasetGroupId && !value.scenarioSource.datasetGroupId) {
        ctx.addIssue({
          code: 'custom',
          path: ['datasetGroupId'],
          message: 'datasetGroupId is required for datasetGroup scenarioSource',
        });
      }
    }
  });

export const CreateEvaluationExperimentResponseSchema = z.object({
  experimentId: z.string(),
  runIds: z.array(z.string()),
  totalRuns: z.number(),
});

export const EvaluationExperimentMetricValueSchema = z.object({
  mean: z.number(),
  median: z.number(),
  stddev: z.number(),
  repeatCount: z.number(),
  sampleCount: z.number(),
  ciLow: z.number().nullable(),
  ciHigh: z.number().nullable(),
});

export const EvaluationExperimentBlockedReasonSchema = z.enum([
  'server_unreachable',
  'waf',
  'rate_limit',
  'anti_bot_system',
  'authentication_failure',
  'credential_disabled',
  'tool_or_environment_error',
  'missing_feature_access',
  'missing_artifacts',
  'guardrails',
  'content_filter',
  'untested',
  'out_of_scope',
  'unknown',
]);

export const EvaluationExperimentBlockedReasonBreakdownSchema = z.object({
  reason: EvaluationExperimentBlockedReasonSchema,
  count: z.number().int().nonnegative(),
});

export const EvaluationExperimentPostAttackConfidenceSchema = z.enum([
  'low',
  'medium',
  'high',
]);

export const EvaluationExperimentFalsePositiveConfidenceBreakdownSchema =
  z.object({
    confidence: EvaluationExperimentPostAttackConfidenceSchema,
    count: z.number().int().nonnegative(),
  });

export const EvaluationExperimentConfigProgressSchema = z.object({
  configName: z.string(),
  completedRuns: z.number(),
  failedRuns: z.number(),
  abortedRuns: z.number(),
  cancelledRuns: z.number(),
  timedOutRuns: z.number(),
  terminalRuns: z.number(),
  totalRuns: z.number(),
  status: z.enum([
    'QUEUED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'ABORTED',
    'CANCELLED',
  ]),
});

export const EvaluationExperimentConfigMetricsSchema = z.object({
  configName: z.string(),
  runCount: z.number(),
  completedRunCount: z.number(),
  failedRunCount: z.number(),
  abortedRunCount: z.number(),
  terminalRunCount: z.number(),
  sampleCount: z.number(),
  labelledSampleCount: z.number(),
  correctCount: z.number(),
  incorrectCount: z.number(),
  scoredSampleCount: z.number(),
  passedCount: z.number(),
  notTestedCount: z.number(),
  truePositives: z.number(),
  predictedPositive: z.number(),
  expectedPositive: z.number(),
  successRate: EvaluationExperimentMetricValueSchema,
  costUsd: EvaluationExperimentMetricValueSchema,
  toolCallsPerSample: EvaluationExperimentMetricValueSchema,
  totalCostUsd: z.number(),
  operationalCostUsd: z.number(),
  benchmarkCostUsd: z.number(),
  labelMatchRate: EvaluationExperimentMetricValueSchema.nullable(),
  precision: EvaluationExperimentMetricValueSchema.nullable(),
  recall: EvaluationExperimentMetricValueSchema.nullable(),
  f1: EvaluationExperimentMetricValueSchema.nullable().optional(),
  blockedRate: EvaluationExperimentMetricValueSchema.nullable(),
  blockedReasonBreakdown: z.array(
    EvaluationExperimentBlockedReasonBreakdownSchema,
  ),
  falsePositiveConfidenceBreakdown: z.array(
    EvaluationExperimentFalsePositiveConfidenceBreakdownSchema,
  ),
});

export const EvaluationRegressionScheduleSchema = z.object({
  enabled: z.boolean(),
  cadence: z.enum(['daily', 'weekly']),
  lastTriggeredAt: z.string().nullable(),
  lastRegressionAt: z.string().nullable(),
});

export const SetEvaluationRegressionScheduleRequestSchema = z.object({
  enabled: z.boolean(),
  cadence: z.enum(['daily', 'weekly']).default('daily'),
});

export const SetEvaluationRegressionScheduleResponseSchema = z.object({
  experimentId: z.string(),
  schedule: EvaluationRegressionScheduleSchema,
});

export const EvaluationExperimentSummarySchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  componentId: z.string().nullable().optional(),
  tenantId: z.string().nullable(),
  name: z.string(),
  datasetId: z.string().nullable(),
  datasetGroupId: z.string().nullable().optional(),
  imageTag: z.string().nullable(),
  runsPerConfig: z.number(),
  configs: z.array(EvaluationExperimentConfigSchema),
  status: EvaluationRunStatusSchema,
  totalRuns: z.number(),
  completedRuns: z.number(),
  failedRuns: z.number(),
  abortedRuns: z.number(),
  cancelledRuns: z.number(),
  timedOutRuns: z.number(),
  terminalRuns: z.number(),
  progressByConfig: z.array(EvaluationExperimentConfigProgressSchema),
  sampleCount: z.number().nullable(),
  baselineConfigName: z.string().nullable(),
  decisionPolicy: EvaluationDecisionPolicySchema.nullable(),
  regressionSchedule: EvaluationRegressionScheduleSchema.nullable(),
  reusedConfigNames: z.array(z.string()).optional(),
  totalCostUsd: z.number().nullable().optional(),
  costSampleCount: z.number().nullable().optional(),
  costMissingCount: z.number().nullable().optional(),
  resetAppBetweenConfigs: z.boolean().optional(),
  povEnvironmentId: z.string().nullable().optional(),
  currentConfigIndex: z.number().nullable().optional(),
  resettingApp: z.boolean().optional(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});

export const EvaluationExperimentConfusionMatrixSchema = z.object({
  configName: z.string(),
  outcomeDescriptorId: z.string(),
  sampleCount: z.number(),
  cells: z.array(
    z.object({
      expectedOutcome: z.string(),
      actualOutcome: z.string(),
      count: z.number(),
      shareOfExpected: z.number(),
    }),
  ),
});

export const EvaluationExperimentVerdictSchema = z.object({
  configName: z.string(),
  baselineConfigName: z.string(),
  metric: EvaluationDecisionMetricSchema,
  verdict: z.enum(['better', 'worse', 'equivalent', 'inconclusive']),
  delta: z.number().nullable(),
  baselineValue: z.number().nullable(),
  challengerValue: z.number().nullable(),
  baselineSampleCount: z.number(),
  challengerSampleCount: z.number(),
  reason: z.string(),
});

export const EvaluationExperimentDetailsSchema =
  EvaluationExperimentSummarySchema.extend({
    metrics: z.array(EvaluationExperimentConfigMetricsSchema),
    confusionMatrices: z.array(EvaluationExperimentConfusionMatrixSchema),
    verdicts: z.array(EvaluationExperimentVerdictSchema),
  });

export const ListEvaluationExperimentsResponseSchema = z.object({
  experiments: z.array(EvaluationExperimentSummarySchema),
});

export const DeleteEvaluationExperimentResponseSchema = z.object({
  deleted: z.boolean(),
  experimentId: z.string(),
  deletedRunCount: z.int().nonnegative(),
});

export const CancelEvaluationExperimentResponseSchema = z.object({
  cancelled: z.boolean(),
  experimentId: z.string(),
  abortedRunCount: z.int().nonnegative(),
});

export const RerunEvaluationExperimentRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
});

export const RetriggerFailedExperimentRunsRequestSchema = z.object({
  reason: z.string().min(1).max(500),
  mode: z
    .enum(['REUSE_TRACE', 'TRIGGER_NEW_FROM_SIGNAL', 'RESUME_THREAD', 'AUTO'])
    .default('AUTO'),
});

export const RetriggerFailedExperimentRunsResponseSchema = z.object({
  acknowledged: z.boolean(),
  retriggeredRunCount: z.int().nonnegative(),
  sourceRunIds: z.array(z.string()),
});

export const ReusableExperimentConfigCandidateSchema = z.object({
  fingerprint: z.string(),
  configName: z.string(),
  sourceExperimentId: z.string(),
  sourceExperimentName: z.string(),
  runCount: z.number(),
  completedAt: z.string().nullable(),
  isFromRelatedDataset: z.boolean().optional(),
  sourceDatasetLabel: z.string().optional(),
});

export const ListReusableExperimentConfigsResponseSchema = z.object({
  experimentId: z.string(),
  configs: z.array(ReusableExperimentConfigCandidateSchema),
});

export const PreviewReusableExperimentConfigsRequestSchema = z.object({
  datasetId: z.string().min(1),
  runsPerConfig: z.int().min(1).max(10).default(1),
  configs: z.array(EvaluationExperimentConfigSchema).min(1).max(10),
  scenarioConfig: z.record(z.string(), z.unknown()).optional(),
});

export const PreviewReusableExperimentConfigsResponseSchema = z.object({
  configs: z.array(ReusableExperimentConfigCandidateSchema),
});

export const ReuseExperimentConfigsRequestSchema = z.object({
  configNames: z.array(z.string().min(1)).min(1).max(10),
});

export const ReuseExperimentConfigsResponseSchema = z.object({
  experimentId: z.string(),
  reusedConfigNames: z.array(z.string()),
  reusedRunCount: z.int().nonnegative(),
});

export type EvaluationExperimentConfig = z.infer<
  typeof EvaluationExperimentConfigSchema
>;
export type EvaluationExperimentSummary = z.infer<
  typeof EvaluationExperimentSummarySchema
>;
export type EvaluationExperimentDetails = z.infer<
  typeof EvaluationExperimentDetailsSchema
>;
export type EvaluationDecisionMetric = z.infer<
  typeof EvaluationDecisionMetricSchema
>;
export type EvaluationDecisionPolicy = z.infer<
  typeof EvaluationDecisionPolicySchema
>;
export type EvaluationExperimentVerdict = z.infer<
  typeof EvaluationExperimentVerdictSchema
>;

export type EvaluationRegressionSchedule = z.infer<
  typeof EvaluationRegressionScheduleSchema
>;

export type EvaluationExperimentMetricValue = z.infer<
  typeof EvaluationExperimentMetricValueSchema
>;
export type EvaluationExperimentConfigMetrics = z.infer<
  typeof EvaluationExperimentConfigMetricsSchema
>;
export type EvaluationExperimentBlockedReason = z.infer<
  typeof EvaluationExperimentBlockedReasonSchema
>;
export type EvaluationExperimentBlockedReasonBreakdown = z.infer<
  typeof EvaluationExperimentBlockedReasonBreakdownSchema
>;
export type EvaluationExperimentPostAttackConfidence = z.infer<
  typeof EvaluationExperimentPostAttackConfidenceSchema
>;
export type EvaluationExperimentFalsePositiveConfidenceBreakdown = z.infer<
  typeof EvaluationExperimentFalsePositiveConfidenceBreakdownSchema
>;

export const EvaluationExperimentSignalRunSchema = z.object({
  passed: z.boolean().nullable(),
  runId: z.string(),
  repeatIndex: z.number(),
  costUsd: z.number().nullable(),
  toolCalls: z.number().nullable(),
  status: z.enum([
    'QUEUED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'ABORTED',
    'CANCELLED',
  ]),
  executionStatus: z.string().nullable(),
  actualOutcome: z.string().nullable(),
  outcomeMatch: z.boolean().nullable(),
  blockReason: z.string().nullable(),
  skipAttackReasonType: z.string().nullable(),
  skipAttackReason: z.string().nullable(),
  failedReason: z.string().nullable().optional(),
  postAttackConfidence:
    EvaluationExperimentPostAttackConfidenceSchema.nullable().optional(),
  matchedRuleIds: z.array(z.string()).optional(),
});

export const EvaluationExperimentSignalSampleResultSchema =
  EvaluationExperimentSignalRunSchema.extend({
    runs: z.array(EvaluationExperimentSignalRunSchema).optional(),
  });

export const EvaluationExperimentSignalSampleSchema = z.object({
  signalId: z.string(),
  datasetId: z.string().nullable().optional(),
  name: z.string().nullable(),
  category: z.string().nullable(),
  expectedOutcome: z.string().nullable(),
  originalAttackThreadId: z.string().nullable().optional(),
  byConfig: z.record(z.string(), EvaluationExperimentSignalSampleResultSchema),
});

export const GetEvaluationExperimentSamplesResponseSchema = z.object({
  experimentId: z.string(),
  samples: z.array(EvaluationExperimentSignalSampleSchema),
});

export type EvaluationExperimentSignalRun = z.infer<
  typeof EvaluationExperimentSignalRunSchema
>;
export type EvaluationExperimentSignalSampleResult = z.infer<
  typeof EvaluationExperimentSignalSampleResultSchema
>;
export type EvaluationExperimentSignalSample = z.infer<
  typeof EvaluationExperimentSignalSampleSchema
>;

export const EvaluationConfigPresetSchema = z.object({
  id: z.string(),
  name: z.string(),
  componentId: z.string().nullable(),
  config: EvaluationExperimentConfigSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const SaveEvaluationConfigPresetRequestSchema = z.object({
  name: z.string().min(1).max(100),
  componentId: z.string().min(1).nullable().optional(),
  config: EvaluationExperimentConfigSchema,
});

export const SaveEvaluationConfigPresetResponseSchema = z.object({
  preset: EvaluationConfigPresetSchema,
});

export const ListEvaluationConfigPresetsResponseSchema = z.object({
  presets: z.array(EvaluationConfigPresetSchema),
});

export const DeleteEvaluationConfigPresetResponseSchema = z.object({
  deleted: z.boolean(),
  presetId: z.string(),
});

export type EvaluationConfigPreset = z.infer<
  typeof EvaluationConfigPresetSchema
>;
export type EvaluationExperimentConfigProgress = z.infer<
  typeof EvaluationExperimentConfigProgressSchema
>;
